import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Descriptions, message } from 'antd';
import { UserOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import IntlMessage from "../../../../components/IntlMessage/IntlMessage";
import AdminUserService from "../../../../services/AdminUserService";

const UserList = () => {
    const { t } = useTranslation();
    const na = t('dashboard.notProvided');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    useEffect(() => {
        fetchUsers(pagination.current, pagination.pageSize);
    }, []);

    const fetchUsers = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await AdminUserService.getAllUsers(page - 1, limit);
            const { list, hasMore, pages } = response.data;
            
            const usersWithProfile = list.map(user => ({
                id: user.id,
                username: user.username,
                fullName: user.profile?.fullName || na,
                iin: user.profile?.iin || na,
                phone: user.profile?.telNumber || na,
                email: user.profile?.email || na,
                registrationAddress: user.profile?.registrationAddress || na,
                residenceAddress: user.profile?.residenceAddress || na,
                businessType: user.profile?.businessType || na,
                organizationName: user.profile?.organizationName || na,
                bin: user.profile?.bin || na,
                position: user.profile?.position || na,
                authorityBasis: user.profile?.authorityBasis || na,
                bankAccount: user.profile?.bankAccount || na,
                bankBik: user.profile?.bankBik || na,
                bankName: user.profile?.bankName || na,
                role: user.role?.roleName || 'user'
            }));
            
            setUsers(usersWithProfile);
            setPagination(prev => ({
                ...prev,
                current: page,
                pageSize: limit,
                total: pages * limit
            }));
        } catch (error) {
            console.error('Error fetching users:', error);
            message.error(<IntlMessage id={'dashboard.admin.userList.fetchError'} fallback={'Не удалось загрузить список пользователей'} />);
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (user) => {
        setSelectedUser(user);
        setDetailModalVisible(true);
    };

    const handleTableChange = (paginationConfig) => {
        fetchUsers(paginationConfig.current, paginationConfig.pageSize);
    };

    const handleRefresh = () => {
        fetchUsers(pagination.current, pagination.pageSize);
    };

    const columns = [
        {
            title: <IntlMessage id={'username'} fallback={'Имя пользователя'} />,
            dataIndex: 'username',
            key: 'username',
        },
        {
            title: <IntlMessage id={'fullName'} fallback={'ФИО'} />,
            dataIndex: 'fullName',
            key: 'fullName',
        },
        {
            title: <IntlMessage id={'email'} fallback={'Email'} />,
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: <IntlMessage id={'details'} fallback={'Детали'} />,
            key: 'details',
            width: 100,
            render: (_, record) => (
                <Button 
                    type="primary" 
                    size="small" 
                    icon={<EyeOutlined />}
                    onClick={() => handleViewDetails(record)}
                >
                    <IntlMessage id={'view'} fallback={'Просмотр'} />
                </Button>
            ),
        },
    ];

    return (
        <>
            <Card 
                title={
                    <>
                        <UserOutlined style={{ marginRight: 8 }} />
                        <IntlMessage id={'dashboard.admin.userList.title'} fallback={'Управление пользователями'} />
                    </>
                }
                extra={
                    <Button 
                        type="default" 
                        icon={<ReloadOutlined />} 
                        onClick={handleRefresh}
                        loading={loading}
                    >
                        <IntlMessage id={'refresh'} fallback={'Обновить'} />
                    </Button>
                }
                style={{ height: '100%' }}
            >
                <Table
                    columns={columns}
                    dataSource={users}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => t('dashboard.admin.userList.totalRecords', { from: range[0], to: range[1], total }),
                    }}
                    onChange={handleTableChange}
                />
            </Card>

            <Modal
                title={<IntlMessage id={'dashboard.admin.userList.userDetails'} fallback={'Детали пользователя'} />}
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setDetailModalVisible(false)}>
                        <IntlMessage id={'close'} fallback={'Закрыть'} />
                    </Button>
                ]}
                width={600}
            >
                {selectedUser && (
                    <Descriptions column={1} bordered>
                        <Descriptions.Item label={<IntlMessage id={'username'} fallback={'Имя пользователя'} />}>
                            {selectedUser.username}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'fullName'} fallback={'ФИО'} />}>
                            {selectedUser.fullName}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'iin'} fallback={'ИИН'} />}>
                            {selectedUser.iin}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'phone'} fallback={'Контактный телефон'} />}>
                            {selectedUser.phone}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'email'} fallback={'Email'} />}>
                            {selectedUser.email}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'registrationAddress'} fallback={'Адрес регистрации'} />}>
                            {selectedUser.registrationAddress}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'residenceAddress'} fallback={'Адрес фактического проживания'} />}>
                            {selectedUser.residenceAddress}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'businessType'} fallback={'Тип хозяйства'} />}>
                            {selectedUser.businessType}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'organizationName'} fallback={'Название организации'} />}>
                            {selectedUser.organizationName}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'bin'} fallback={'БИН'} />}>
                            {selectedUser.bin}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'position'} fallback={'Должность'} />}>
                            {selectedUser.position}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'authorityBasis'} fallback={'Основание полномочий'} />}>
                            {selectedUser.authorityBasis}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'bankAccount'} fallback={'ИИК (банковский счет)'} />}>
                            {selectedUser.bankAccount}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'bankBik'} fallback={'БИК банка'} />}>
                            {selectedUser.bankBik}
                        </Descriptions.Item>
                        <Descriptions.Item label={<IntlMessage id={'bankName'} fallback={'Название банка'} />}>
                            {selectedUser.bankName}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </>
    );
};

export default UserList;